import { ReactNode, useCallback, useMemo } from 'react';
import { useToggler } from '@cbhq/cds-common';
import { Button, ButtonGroup } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import {
  TextBody,
  TextDisplay3,
  TextHeadline,
  TextTitle1,
  TextTitle3,
  TextTitle4,
} from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { BaseL3Chain } from ':cloud/types/ts_types';
import { BASE_APPCHAIN_DISCORD_LINK, BASE_APPCHAINS_DOCS_LINK } from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';

import { FULL_PAGE_MAX_WIDTH } from '../product/constants';

import { BaseAppchainLinkCards } from './BaseAppchainLinkCards';
import { ConfigModal } from './ConfigModal';
import { BaseAppchainStatusEnum, BaseAppchainTypeEnum, BaseNetworkEnum } from './constants';
import { ContactUsModal } from './ContactUsModal';
import { DeploymentModal } from './DeploymentModal';
import { DetailItem } from './DetailItem';
import { WhitelistModal } from './WhitelistModal';

type BaseAppchainsOverviewItemProps = {
  children?: ReactNode;
  title?: string | Element;
  description?: string;
  borderedBottom?: boolean;
  isHeader?: boolean;
};

function BaseAppchainsOverviewItem({
  children,
  title,
  description,
  isHeader = false,
  borderedBottom = true,
}: BaseAppchainsOverviewItemProps) {
  const header = useMemo(() => {
    if (isHeader && title) {
      return <TextTitle1 as="h3">{title}</TextTitle1>;
    }
    if (title) {
      return <TextTitle3 as="h3">{title}</TextTitle3>;
    }
    return null;
  }, [isHeader, title]);

  return (
    <VStack spacing={7} borderedBottom={borderedBottom} gap={1}>
      {header}
      {description && (
        <TextBody as="p" color="foregroundMuted" spacingBottom={2}>
          {description}
        </TextBody>
      )}
      {children}
    </VStack>
  );
}

type BaseAppchainsOverviewTabProps = {
  chains?: BaseL3Chain[];
  selectedChain?: BaseL3Chain;
};
export function BaseAppchainsOverviewTab({ selectedChain, chains }: BaseAppchainsOverviewTabProps) {
  const { isDesktop } = useSimpleBreakpoints();

  const [contactUsModalVisible, { toggleOn: showContactUsModal, toggleOff: hideContactUsModal }] =
    useToggler(false);
  const [whitelistModalVisible, { toggleOn: showWhitelistModal, toggleOff: hideWhitelistModal }] =
    useToggler(false);
  const [configModalVisible, { toggleOn: showConfigModal, toggleOff: hideConfigModal }] =
    useToggler(false);
  const [
    deploymentModalVisible,
    { toggleOn: showDeploymentModal, toggleOff: hideDeploymentModal },
  ] = useToggler(false);

  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '1fr 1fr' : '1fr';
  }, [isDesktop]);

  const mainnet = useMemo(() => {
    return chains?.find((chain) => chain?.network === BaseNetworkEnum.Mainnet);
  }, [chains]);

  const handleDeployToMainnetPress = useCallback(() => {
    if (mainnet) {
      showDeploymentModal();
      return;
    }
    showContactUsModal();
  }, [mainnet, showContactUsModal, showDeploymentModal]);

  useLogViewOnMount(EventName.base_appchains_overview_tab_view);

  const isTestnet = selectedChain?.network === BaseNetworkEnum?.Testnet;

  const hasLaunchedMainnet =
    chains?.find((chain) => chain?.network === BaseNetworkEnum.Mainnet)?.status ===
    BaseAppchainStatusEnum.Running;

  const headerText = useMemo(() => {
    if (!hasLaunchedMainnet) {
      return 'Your appchain is on testnet. When you’re ready, launch to mainnet.';
    }
    if (isTestnet) {
      return 'Your appchain is on testnet.';
    }
    return 'Your appchain is on mainnet.';
  }, [hasLaunchedMainnet, isTestnet]);

  const descriptionText = useMemo(() => {
    if (!hasLaunchedMainnet) {
      return 'Unlock fast and cheap transactions at scale, with dedicated blockspace and seamless integration with Base tools.';
    }
    return 'Fast and cheap transactions at scale, with dedicated blockspace and seamless integration with Base tools.';
  }, [hasLaunchedMainnet]);

  const permissionsDescription = useMemo(() => {
    if (selectedChain?.customizations?.contractAllowlist?.length) {
      return 'You can whitelist which addresses are able to deploy contracts on your Base Appchain.';
    }
    return 'Any address can deploy on your Base Appchain. Or, you can whitelist which addresses are able to deploy contracts on your Base Appchain.';
  }, [selectedChain?.customizations?.contractAllowlist?.length]);

  return (
    <Box display="block" height="100%" flexGrow={1}>
      <ContactUsModal isVisible={contactUsModalVisible} onClose={hideContactUsModal} />
      <ConfigModal chain={selectedChain} isVisible={configModalVisible} onClose={hideConfigModal} />
      <DeploymentModal
        isVisible={deploymentModalVisible}
        onClose={hideDeploymentModal}
        chain={mainnet}
      />
      <WhitelistModal
        isVisible={whitelistModalVisible}
        onClose={hideWhitelistModal}
        rollupName={selectedChain?.name}
        chain={selectedChain}
      />
      <VStack spacingBottom={10} maxWidth="100%">
        <VStack width="100%">
          {!hasLaunchedMainnet && !!mainnet && (
            <VStack spacing={7} spacingBottom={0} width="100%">
              <HStack
                borderRadius="rounded"
                spacing={4}
                bordered
                borderColor="primary"
                gap={2}
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                maxWidth={FULL_PAGE_MAX_WIDTH}
              >
                <VStack gap={2}>
                  <HStack alignItems="center" gap={2}>
                    <Box>
                      <Icon name="blockchain" color="primary" size="s" />
                    </Box>
                    <TextHeadline as="p" color="primary">
                      Mainnet
                    </TextHeadline>
                  </HStack>
                  <TextBody as="p" color="primary">
                    Deploy your L3 on Base Mainnet. Best used as a production environment with real
                    economic value.
                  </TextBody>
                </VStack>
                <Box minWidth={120}>
                  <Button onPress={handleDeployToMainnetPress}>Deploy your mainnet</Button>
                </Box>
              </HStack>
            </VStack>
          )}
          <HStack spacing={7}>
            <Grid
              maxWidth={FULL_PAGE_MAX_WIDTH}
              templateColumns={responsiveTemplateColumns}
              gap={9}
              spacingTop={4}
              width="100%"
            >
              <VStack justifyContent="center">
                <TextDisplay3 as="h1">{headerText}</TextDisplay3>
                <TextTitle4 as="h2" color="foregroundMuted" spacingTop={1} spacingBottom={7}>
                  {descriptionText}
                </TextTitle4>

                <ButtonGroup>
                  <Button
                    variant="primary"
                    endIcon="diagonalUpArrow"
                    onPress={onExternalLinkPress(BASE_APPCHAINS_DOCS_LINK)}
                    accessibilityLabel="View doc"
                  >
                    View docs
                  </Button>
                  <Button
                    variant="secondary"
                    endIcon="diagonalUpArrow"
                    onPress={onExternalLinkPress(BASE_APPCHAIN_DISCORD_LINK)}
                    accessibilityLabel="Discord channel + opens in new window"
                  >
                    Discord channel
                  </Button>
                </ButtonGroup>
              </VStack>
            </Grid>
          </HStack>
        </VStack>

        <VStack>
          <BaseAppchainsOverviewItem borderedBottom>
            <BaseAppchainLinkCards chain={selectedChain} />
          </BaseAppchainsOverviewItem>

          {/* Permission feature not allowed on shared testnet  */}
          {selectedChain?.type !== BaseAppchainTypeEnum.SharedTestnet && (
            <BaseAppchainsOverviewItem title="Permissions" description={permissionsDescription}>
              {!!selectedChain?.customizations?.contractAllowlist?.length && (
                <VStack gap={4} spacingBottom={4} maxWidth={700}>
                  {selectedChain?.customizations?.contractAllowlist?.map((item, index) => (
                    <DetailItem
                      label={`Address ${index + 1}`}
                      value={item?.address}
                      showCopy
                      key={item.address}
                    />
                  ))}
                </VStack>
              )}
              <Box>
                <Button variant="secondary" onPress={showWhitelistModal}>
                  Add whitelist address
                </Button>
              </Box>
            </BaseAppchainsOverviewItem>
          )}

          <BaseAppchainsOverviewItem
            borderedBottom={false}
            title="Danger zone"
            description="You can export and move your Base Appchains off the Coinbase Developer Platform."
          >
            <Box>
              <Button variant="secondary" onPress={showConfigModal}>
                Export config
              </Button>
            </Box>
          </BaseAppchainsOverviewItem>
        </VStack>
      </VStack>
    </Box>
  );
}
